import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  countEntityLinks,
  describeLinkLoss,
  withholdLinkLosingDescriptions,
} from '../../services/descriptionLinkGuard';
import type {
  BrandStatus,
  DocsLink,
  MovementManufacturer,
  WatchBrand,
} from '../../types';
import { cleanDocsLinks } from '../../utils/docsLinks';
import {
  deriveManufacturerSlug,
  getWatchBrands,
  uploadManufacturerImage,
  type ManufacturerCatalogueEntry,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { EnrichHintDialog } from '../common/EnrichHintDialog';
import { FormSection } from '../common/FormSection';
import { Badge } from '../common/Badge';
import { HeritagePicker } from '../common/HeritagePicker';
import { CountrySelect } from '../common/CountrySelect';
import { DocsLinksEditor } from '../common/DocsLinksEditor';
import { TagInput } from '../CuratedWatches/TagInput';
import { ParentOrgPicker } from '../ParentOrganizations/ParentOrgPicker';
import {
  EMPTY_PARENT_ORG_INDEX,
  parentOrgFromText,
  type ParentOrgIndex,
} from '../ParentOrganizations/parentOrgResolve';
import { enrichMovementManufacturerWithAI } from '../../services/firebase';
import { RichTextEditor } from '../common/RichTextEditor';
import {
  htmlToPlainText,
  plainTextToHtml,
  richDescriptionPatch,
  richDescriptionSeed,
} from '../../services/descriptionHtml';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import { CalibreFamiliesSection } from './CalibreFamiliesSection';
import { ManufacturerDataHealthSection } from './ManufacturerDataHealthSection';
import { ElectronicModulesSection } from '../ElectronicModules';
import { ManufacturerTimelineSection } from './ManufacturerTimelineSection';
import { ManufacturerTierNamesSection } from './ManufacturerTierNamesSection';
import { RevisionLinesSection } from './RevisionLinesSection';
import { RevisionTermsField } from './RevisionTermsField';
import { revisionTerms } from '../../services/revisionTerms';

export interface MovementManufacturerDraft {
  name: string;
  displayName?: string;
  description?: string;
  /** Rich description (TipTap HTML). Saved with its plain text in `description`. */
  descriptionRich?: string;
  aiDescription?: string;
  alternativeNames?: string[];
  abbreviations?: string[];
  logo?: string;
  logoSourceUrl?: string;
  heroImage?: string;
  heroImageSourceUrl?: string;
  country?: string;
  founded?: number;
  website?: string;
  parentOrg?: string;
  /** Dual-written beside `parentOrg` by the parent-org picker. */
  parentOrgId?: string;
  isGeneric?: boolean;
  parentManufacturerId?: string;
  brandIdsManualInclude?: string[];
  status?: BrandStatus;
  needsReview?: boolean;
  /** Lineage gate: shows the Revision lines section and each calibre's Revisions card. */
  supportsLineage?: boolean;
  /** The maker's word for a revision ("Revision Code"); blank = the default "Revision". */
  revisionTermSingular?: string;
  revisionTermPlural?: string;
  heritageBrandIds?: string[];
  yearCeased?: number;
  // --- Identity + location detail (parity with WatchBrand) ---
  city?: string;
  stateRegion?: string;
  assemblyLocation?: string;
  instagramUrl?: string;
  founder?: { name?: string };
  ownershipType?: MovementManufacturer['ownershipType'];
  yearRevived?: number;
  predecessorManufacturer?: string;
  /**
   * Curator documentation links. Whitelisted here deliberately: the draft is the
   * full set of keys Save writes back, so a field left off this interface is
   * simply never persisted — it isn't cleared, it just can't be edited.
   */
  docs?: DocsLink[];
}

interface MovementManufacturerDetailProps {
  initial?: MovementManufacturer | null;
  /** Existing slug for edit mode. Ignored in create mode (slug derives from `name`). */
  existingSlug?: string;
  isNew: boolean;
  /**
   * Other manufacturers — drives the parent picker and the "listed as heritage
   * by" warning. Exclude `initial`.
   *
   * Catalogue ENTRIES, not documents: this is the listing's in-memory Algolia
   * array, and the picker only needs `id` / `name` / `displayName` /
   * `isGeneric` / `heritageBrandIds`. `initial` stays a whole document — that
   * one is round-tripped on Save.
   */
  parentOptions: ManufacturerCatalogueEntry[];
  onCancel: () => void;
  onSave: (draft: MovementManufacturerDraft) => Promise<void>;
  saving: boolean;
  /** Data Health quick actions write immediately; this bubbles the change up. */
  onManufacturerChange?: (updated: MovementManufacturer) => void;
  /** `parent_organizations`, indexed — the parent-org picker's options. */
  parentOrgIndex?: ParentOrgIndex;
  parentOrgsLoading?: boolean;
  parentOrgsError?: boolean;
}

/**
 * Logo / hero image field: upload button + thumbnail preview, with a manual URL
 * input as a fallback. Uploads go to Firebase Storage and store the resulting
 * public download URL (the manufacturer doc keeps these as plain URLs).
 */
function ManufacturerImageField({
  label,
  field,
  value,
  manufacturerId,
  onChange,
  sourceUrl,
  onSourceUrlChange,
}: {
  label: string;
  field: 'logo' | 'hero';
  value: string;
  manufacturerId: string;
  onChange: (url: string) => void;
  sourceUrl: string;
  onSourceUrlChange: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (!manufacturerId) {
      toast.error('Enter a name first so the manufacturer has an id');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const url = await uploadManufacturerImage(manufacturerId, field, file, file.name);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      console.error(`${label} upload failed`, err);
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-start gap-3">
        {value ? (
          <img
            src={value}
            alt={label}
            className="w-16 h-16 object-contain rounded border border-gray-200 bg-gray-50 shrink-0"
            onError={(e) => {
              (e.target as HTMLImageElement).style.opacity = '0.3';
            }}
          />
        ) : (
          <div className="w-16 h-16 rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-[10px] text-gray-400 text-center px-1 shrink-0">
            No image
          </div>
        )}
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
              className="text-xs text-gray-600"
            />
            {uploading && <span className="text-xs text-gray-400">Uploading…</span>}
            {value && !uploading && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Remove
              </button>
            )}
          </div>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https:// or upload above"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {label} source URL
            </label>
            <input
              type="url"
              value={sourceUrl}
              onChange={(e) => onSourceUrlChange(e.target.value)}
              placeholder="https://..."
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function MovementManufacturerDetail({
  initial,
  existingSlug,
  isNew,
  parentOptions,
  onCancel,
  onSave,
  saving,
  onManufacturerChange,
  parentOrgIndex = EMPTY_PARENT_ORG_INDEX,
  parentOrgsLoading = false,
  parentOrgsError = false,
}: MovementManufacturerDetailProps) {
  // What the description editor opened with — an untouched editor writes nothing.
  const [descriptionSeed] = useState(() => richDescriptionSeed(initial ?? {}));
  const [draft, setDraft] = useState<MovementManufacturerDraft>(() => ({
    name: initial?.name ?? '',
    displayName: initial?.displayName ?? '',
    description: initial?.description ?? '',
    // The editor works on HTML: the stored rich text, else the plain text as paragraphs.
    descriptionRich: richDescriptionSeed(initial ?? {}),
    aiDescription: initial?.aiDescription ?? '',
    alternativeNames: initial?.alternativeNames ?? [],
    abbreviations: initial?.abbreviations ?? [],
    logo: initial?.logo ?? '',
    logoSourceUrl: initial?.logoSourceUrl ?? '',
    heroImage: initial?.heroImage ?? '',
    heroImageSourceUrl: initial?.heroImageSourceUrl ?? '',
    country: initial?.country ?? '',
    founded: initial?.founded,
    website: initial?.website ?? '',
    parentOrg: initial?.parentOrg ?? '',
    parentOrgId: initial?.parentOrgId ?? '',
    // isGeneric & brandIdsManualInclude are intentionally NOT seeded into the
    // draft — Data Health quick actions write them out-of-band, so the form
    // derives them from the live `initial` prop + in-form deltas instead (see
    // below). Seeding them here would resurrect the sync race PR #290/#291 hit.
    parentManufacturerId: initial?.parentManufacturerId ?? '',
    status: initial?.status ?? 'active',
    needsReview: initial?.needsReview ?? false,
    supportsLineage: initial?.supportsLineage === true,
    revisionTermSingular: initial?.revisionTermSingular ?? '',
    revisionTermPlural: initial?.revisionTermPlural ?? '',
    heritageBrandIds: initial?.heritageBrandIds ?? [],
    yearCeased: initial?.yearCeased,
    city: initial?.city ?? '',
    stateRegion: initial?.stateRegion ?? '',
    assemblyLocation: initial?.assemblyLocation ?? '',
    instagramUrl: initial?.instagramUrl ?? '',
    founder: initial?.founder ? { name: initial.founder.name ?? '' } : { name: '' },
    ownershipType: initial?.ownershipType,
    yearRevived: initial?.yearRevived,
    predecessorManufacturer: initial?.predecessorManufacturer ?? '',
    docs: initial?.docs ?? [],
  }));

  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);

  const update = <K extends keyof MovementManufacturerDraft>(
    key: K,
    value: MovementManufacturerDraft[K],
  ) => setDraft((d) => ({ ...d, [key]: value }));

  // --- Data-Health-written fields: derive, never sync into draft --------------
  //
  // `brandIdsManualInclude` and `isGeneric` are written straight to Firestore by
  // the Data Health quick actions (promote/demote/exclude, mark generic),
  // out-of-band from this form. The parent keeps the `initial` prop current via
  // handleManufacturerPatched, so `initial` is the freshest in-memory value at
  // any moment — including at Save time.
  //
  // We deliberately do NOT copy those writes back into `draft`. PR #290/#291
  // tried that with a guarded useEffect and it raced intermittently: the guard
  // reads a ref (`prevInitial*.current`) that the effect body mutates
  // synchronously, while the guard itself runs inside a *deferred* functional
  // setDraft updater. Whether the updater sees the pre- or post-mutation ref
  // value depends on how React batches the surrounding renders, so the promoted
  // brand was sometimes adopted (retained on Save) and sometimes not (clobbered).
  //
  // Instead: treat the live `initial` value as the authoritative base and
  // overlay only the user's own in-form edits. Read both here (for display) and
  // in handleSubmit (for the payload) — no synced draft state, no race.
  const freshInclude = useMemo(
    () => initial?.brandIdsManualInclude ?? [],
    [initial?.brandIdsManualInclude],
  );
  // The user's Allowed-Brands-picker edits this session, as a delta against the
  // live base set. Recomputed wholesale on every picker change, so a later Data
  // Health write to `freshInclude` still merges cleanly (base moves, delta holds).
  const [includeAdds, setIncludeAdds] = useState<Set<string>>(new Set());
  const [includeRemoves, setIncludeRemoves] = useState<Set<string>>(new Set());
  const effectiveInclude = useMemo(() => {
    const s = new Set(freshInclude);
    for (const id of includeAdds) s.add(id);
    for (const id of includeRemoves) s.delete(id);
    return Array.from(s).sort();
  }, [freshInclude, includeAdds, includeRemoves]);
  const handleIncludeChange = (next: string[]) => {
    const nextSet = new Set(next);
    const freshSet = new Set(freshInclude);
    const adds = new Set<string>();
    const removes = new Set<string>();
    for (const id of nextSet) if (!freshSet.has(id)) adds.add(id);
    for (const id of freshSet) if (!nextSet.has(id)) removes.add(id);
    setIncludeAdds(adds);
    setIncludeRemoves(removes);
  };

  // `null` = untouched this session (defer to the live `initial` value); a
  // boolean = the user's explicit checkbox choice (their intent wins over a
  // later Data Health write). Untouched is the common case, so no clobber.
  const [isGenericOverride, setIsGenericOverride] = useState<boolean | null>(null);
  const isGenericValue = isGenericOverride ?? initial?.isGeneric ?? false;

  const handleNameChange = (value: string) => {
    if (isNew) setNameManuallyEdited(true);
    update('name', value);
  };

  const handleDisplayNameChange = (value: string) => {
    setDraft((d) => {
      const next: MovementManufacturerDraft = { ...d, displayName: value };
      if (isNew && !nameManuallyEdited) {
        next.name = value;
      }
      return next;
    });
  };

  const computedSlug = isNew
    ? deriveManufacturerSlug(draft.name)
    : existingSlug ?? '';

  const canSave = computedSlug.length > 0;

  // AI enrichment state
  const [aiLoading, setAiLoading] = useState(false);
  // The Enrich-with-AI hint dialog; the hint goes to this run only.
  const [showEnrichHint, setShowEnrichHint] = useState(false);
  const [enrichHint, setEnrichHint] = useState('');
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, unknown>>({});
  const [aiConfidence, setAiConfidence] = useState<Record<string, 'high' | 'medium' | 'low'>>({});
  const [aiAccepted, setAiAccepted] = useState<Record<string, boolean>>({});

  const aiFieldHandlers: Record<
    string,
    { label: string; currentDisplay: string; apply: (v: unknown) => void; formatSuggested?: (v: unknown) => string }
  > = {
    description: {
      label: 'Description',
      currentDisplay: htmlToPlainText(draft.descriptionRich),
      // The AI answers in plain text; it lands in the rich editor as paragraphs.
      apply: (v) => typeof v === 'string' && update('descriptionRich', plainTextToHtml(v)),
    },
    aiDescription: {
      label: 'AI Description',
      currentDisplay: draft.aiDescription ?? '',
      apply: (v) => typeof v === 'string' && update('aiDescription', v),
    },
    // The rich (HTML) twin of aiDescription — allowlist-cleaned server-side.
    // Same editor as `description`; when both are ticked the rich one wins
    // (see handleApplyAISuggestions).
    descriptionRich: {
      label: 'Description (rich)',
      currentDisplay: htmlToPlainText(draft.descriptionRich),
      apply: (v) => typeof v === 'string' && update('descriptionRich', v),
      formatSuggested: (v) => (typeof v === 'string' ? htmlToPlainText(v) : ''),
    },
    country: {
      label: 'Country',
      currentDisplay: draft.country ?? '',
      apply: (v) => typeof v === 'string' && update('country', v),
    },
    founded: {
      label: 'Founded',
      currentDisplay: draft.founded ? String(draft.founded) : '',
      apply: (v) => {
        if (typeof v === 'number') update('founded', v);
        else if (typeof v === 'string' && v.trim()) {
          const n = parseInt(v.trim(), 10);
          if (!isNaN(n)) update('founded', n);
        }
      },
    },
    parentOrg: {
      label: 'Parent organization',
      currentDisplay: draft.parentOrg ?? '',
      apply: (v) => {
        if (typeof v !== 'string') return;
        // Links by id only when the suggestion names exactly one organization.
        const next = parentOrgFromText(v, parentOrgIndex);
        update('parentOrg', next.parentOrg);
        update('parentOrgId', next.parentOrgId);
      },
    },
    website: {
      label: 'Website',
      currentDisplay: draft.website ?? '',
      apply: (v) => typeof v === 'string' && update('website', v),
    },
    city: {
      label: 'City',
      currentDisplay: draft.city ?? '',
      apply: (v) => typeof v === 'string' && update('city', v),
    },
    stateRegion: {
      label: 'State / region',
      currentDisplay: draft.stateRegion ?? '',
      apply: (v) => typeof v === 'string' && update('stateRegion', v),
    },
    assemblyLocation: {
      label: 'Assembly location',
      currentDisplay: draft.assemblyLocation ?? '',
      apply: (v) => typeof v === 'string' && update('assemblyLocation', v),
    },
    instagramUrl: {
      label: 'Instagram URL',
      currentDisplay: draft.instagramUrl ?? '',
      apply: (v) => typeof v === 'string' && update('instagramUrl', v),
    },
    'founder.name': {
      label: 'Founder',
      currentDisplay: draft.founder?.name ?? '',
      apply: (v) => typeof v === 'string' && update('founder', { name: v }),
    },
    ownershipType: {
      label: 'Ownership type',
      currentDisplay: draft.ownershipType ?? '',
      apply: (v) => {
        if (typeof v !== 'string') return;
        const allowed: NonNullable<MovementManufacturer['ownershipType']>[] = [
          'independent',
          'family-owned',
          'private-equity',
          'public',
          'subsidiary',
        ];
        if ((allowed as string[]).includes(v)) {
          update('ownershipType', v as NonNullable<MovementManufacturer['ownershipType']>);
        }
      },
    },
    status: {
      label: 'Status',
      currentDisplay: draft.status ?? '',
      apply: (v) => {
        if (typeof v !== 'string') return;
        const allowed: BrandStatus[] = ['active', 'dormant', 'defunct', 'revived', 'absorbed'];
        if ((allowed as string[]).includes(v)) update('status', v as BrandStatus);
      },
    },
    yearRevived: {
      label: 'Year revived',
      currentDisplay: draft.yearRevived ? String(draft.yearRevived) : '',
      apply: (v) => {
        if (typeof v === 'number') update('yearRevived', v);
        else if (typeof v === 'string' && /^\d{4}$/.test(v.trim())) update('yearRevived', Number(v.trim()));
      },
    },
    yearCeased: {
      label: 'Year ceased',
      currentDisplay: draft.yearCeased ? String(draft.yearCeased) : '',
      apply: (v) => {
        if (typeof v === 'number') update('yearCeased', v);
        else if (typeof v === 'string' && /^\d{4}$/.test(v.trim())) update('yearCeased', Number(v.trim()));
      },
    },
    predecessorManufacturer: {
      label: 'Predecessor manufacturer',
      currentDisplay: draft.predecessorManufacturer ?? '',
      apply: (v) => typeof v === 'string' && update('predecessorManufacturer', v),
    },
  };

  const handleAIEnrich = async (hint?: string) => {
    if (!draft.name.trim()) {
      toast.error('Enter a name before enriching');
      return;
    }
    setAiLoading(true);
    try {
      const result = await enrichMovementManufacturerWithAI({
        // Always send the live draft so unsaved edits inform the prompt.
        manufacturerData: {
          name: draft.name.trim(),
          displayName: draft.displayName?.trim() || undefined,
          description: htmlToPlainText(draft.descriptionRich) || undefined,
          // The HTML too: the model can only keep the in-app links it is shown.
          descriptionRich: htmlToPlainText(draft.descriptionRich) ? draft.descriptionRich : undefined,
          aiDescription: draft.aiDescription?.trim() || undefined,
          country: draft.country?.trim() || undefined,
          founded: draft.founded || undefined,
          website: draft.website?.trim() || undefined,
          parentOrg: draft.parentOrg?.trim() || undefined,
          city: draft.city?.trim() || undefined,
          stateRegion: draft.stateRegion?.trim() || undefined,
          assemblyLocation: draft.assemblyLocation?.trim() || undefined,
          instagramUrl: draft.instagramUrl?.trim() || undefined,
          founder: draft.founder?.name?.trim()
            ? { name: draft.founder.name.trim() }
            : undefined,
          ownershipType: draft.ownershipType,
          status: draft.status,
          yearRevived: draft.yearRevived,
          yearCeased: draft.yearCeased,
          predecessorManufacturer: draft.predecessorManufacturer?.trim() || undefined,
        },
        additionalContext: hint,
      });

      // A proposed description that drops any in-app link of the current one is
      // never offered — checked here as well as server-side (descriptionLinkGuard).
      // The plain `description` suggestion can carry no link at all.
      const guarded = withholdLinkLosingDescriptions(result, draft.descriptionRich, {
        html: ['descriptionRich'],
        plain: ['description'],
      });
      const linkLoss = describeLinkLoss(guarded.losses);
      if (linkLoss) toast.error(linkLoss, { duration: 12000 });
      const suggestions = { ...guarded.result.suggestions };
      const confidence = { ...guarded.result.confidence };

      if (Object.keys(suggestions).length === 0) {
        toast('No new suggestions — fields look complete');
        return;
      }

      // Default-accept high-confidence suggestions
      const accepted: Record<string, boolean> = {};
      for (const key of Object.keys(suggestions)) {
        accepted[key] = confidence[key] === 'high';
      }

      setAiSuggestions(suggestions);
      setAiConfidence(confidence);
      setAiAccepted(accepted);
      setShowAISuggestions(true);
    } catch (err) {
      console.error('AI enrichment failed:', err);
      toast.error(err instanceof Error ? err.message : 'AI enrichment failed');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAISuggestions = () => {
    let applied = 0;
    for (const [key, value] of Object.entries(aiSuggestions)) {
      if (!aiAccepted[key]) continue;
      // `description` and `descriptionRich` fill the same editor; the rich one
      // keeps its formatting, so it wins when both are ticked.
      if (key === 'description' && aiAccepted.descriptionRich && typeof aiSuggestions.descriptionRich === 'string') {
        continue;
      }
      const handler = aiFieldHandlers[key];
      if (!handler) continue;
      handler.apply(value);
      applied++;
    }
    setShowAISuggestions(false);
    if (applied > 0) {
      toast.success(`Applied ${applied} AI suggestion${applied === 1 ? '' : 's'}`);
    }
  };

  const handleSubmit = async () => {
    if (!canSave) return;
    const trimmed: MovementManufacturerDraft = {
      name: draft.name.trim(),
      displayName: draft.displayName?.trim() || undefined,
      // Only when the rich editor changed: HTML + its plain text, together (an
      // emptied editor emits '' for a stored field — the save turns that into a
      // delete). Untouched: both stay undefined, so nothing is rewritten.
      ...((draft.descriptionRich ?? '') !== descriptionSeed
        ? richDescriptionPatch(draft.descriptionRich ?? '', initial ?? {})
        : { description: undefined, descriptionRich: undefined }),
      aiDescription: draft.aiDescription?.trim() || undefined,
      alternativeNames:
        draft.alternativeNames && draft.alternativeNames.length > 0
          ? draft.alternativeNames.map((a) => a.trim()).filter(Boolean)
          : undefined,
      abbreviations:
        draft.abbreviations && draft.abbreviations.length > 0
          ? draft.abbreviations.map((a) => a.trim()).filter(Boolean)
          : undefined,
      logo: draft.logo?.trim() || undefined,
      logoSourceUrl: draft.logoSourceUrl?.trim() || undefined,
      heroImage: draft.heroImage?.trim() || undefined,
      heroImageSourceUrl: draft.heroImageSourceUrl?.trim() || undefined,
      country: draft.country?.trim() || undefined,
      founded: draft.founded || undefined,
      website: draft.website?.trim() || undefined,
      parentOrg: draft.parentOrg?.trim() || undefined,
      parentOrgId: draft.parentOrgId?.trim() || undefined,
      // Persist an EXPLICIT boolean, never undefined. `false || undefined` is
      // `undefined`, which stripUndefined drops — so unchecking the box used to
      // write nothing and the field stayed absent in Firestore (indistinguishable
      // from "never classified"). Downstream logic (reclassifyCoDevelopedCalibres,
      // AKA brand-inference) treats absent ≠ false, so absence blocked linking.
      isGeneric: Boolean(isGenericValue),
      // Default `active` is stored as absent (see BrandStatus). Reverting a
      // previously-marked manufacturer back to active is handled in the
      // editor's save (deleteField), since stripUndefined can't clear it here.
      status: draft.status === 'active' ? undefined : draft.status,
      // Needs-review audit flag: `true` when set, undefined when unchecked so
      // it's stripped (and cleared via deleteField in the editor's save when
      // previously set); mirrors the status convention.
      needsReview: draft.needsReview ? true : undefined,
      // Same convention: true or stripped; the editor's save deletes a stored
      // true when it is switched off.
      supportsLineage: draft.supportsLineage ? true : undefined,
      // Blank ⇒ undefined (stripped, and cleared via deleteField in the
      // editor's save when previously set). Kept while lineage is off, so
      // switching the gate off and on does not lose the curator's words.
      revisionTermSingular: draft.revisionTermSingular?.trim() || undefined,
      revisionTermPlural: draft.revisionTermPlural?.trim() || undefined,
      parentManufacturerId: draft.parentManufacturerId?.trim() || undefined,
      // Authoritative value = live `initial` base + this session's picker delta
      // (see the derivation block above). Never read from a synced draft.
      brandIdsManualInclude: Array.from(
        new Set(effectiveInclude.map((b) => b.trim()).filter(Boolean)),
      ).sort(),
      // Empty ⇒ undefined so it's stripped (and cleared via deleteField in the
      // editor's save when previously set); mirrors the status convention.
      heritageBrandIds:
        (draft.heritageBrandIds ?? []).length > 0
          ? Array.from(new Set(draft.heritageBrandIds ?? [])).sort()
          : undefined,
      // Year ceased is only meaningful for ceased manufacturers (defunct |
      // dormant | absorbed) — drop it for any other status so it's stripped (and
      // cleared via deleteField in the editor's save when previously set).
      // Advisory validation only otherwise.
      yearCeased:
        draft.status === 'defunct' ||
        draft.status === 'dormant' ||
        draft.status === 'absorbed'
          ? draft.yearCeased
          : undefined,
      // Identity + location detail — all optional, trimmed, empty ⇒ undefined so
      // stripUndefined drops them. The editor's save clears the stored field with
      // deleteField when the draft reports undefined but the previous document
      // held a value, mirroring the yearCeased convention.
      city: draft.city?.trim() || undefined,
      stateRegion: draft.stateRegion?.trim() || undefined,
      assemblyLocation: draft.assemblyLocation?.trim() || undefined,
      instagramUrl: draft.instagramUrl?.trim() || undefined,
      founder: draft.founder?.name?.trim()
        ? { name: draft.founder.name.trim() }
        : undefined,
      ownershipType: draft.ownershipType,
      // yearRevived is only meaningful on a revived manufacturer — drop it for
      // every other status so stripUndefined removes a stale value.
      yearRevived: draft.status === 'revived' ? draft.yearRevived : undefined,
      predecessorManufacturer:
        draft.status === 'revived' ? draft.predecessorManufacturer?.trim() || undefined : undefined,
      // Docs links: blank rows dropped, empty ⇒ undefined so it's stripped (and
      // cleared via deleteField in the editor's save when previously set);
      // mirrors the heritage convention.
      docs: (() => {
        const cleaned = cleanDocsLinks(draft.docs ?? []);
        return cleaned.length > 0 ? cleaned : undefined;
      })(),
    };
    await onSave(trimmed);
  };

  return (
    <div className="space-y-4">
      <FormSection title="Identity">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Canonical name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="e.g., ETA SA Manufacture Horlogère Suisse"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Slug (doc id):{' '}
            <span className="font-mono text-gray-700">
              {computedSlug || <em>—</em>}
            </span>
            {isNew ? ' (derived from name)' : ' (locked)'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Display name
          </label>
          <input
            type="text"
            value={draft.displayName ?? ''}
            onChange={(e) => handleDisplayNameChange(e.target.value)}
            placeholder="e.g., ETA"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <TagInput
          label="Alternative names (matching during backfill / imports)"
          values={draft.alternativeNames ?? []}
          onChange={(v) => update('alternativeNames', v)}
          placeholder="Add a variant spelling and press Enter…"
        />

        <TagInput
          label="Abbreviations"
          values={draft.abbreviations ?? []}
          onChange={(v) => update('abbreviations', v)}
          placeholder="Add an abbreviation and press Enter…"
        />
      </FormSection>

      <FormSection title="Description">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          {/* Rich text → descriptionRich; its plain text is saved to description
              too, for app builds without the rich renderer. */}
          <RichTextEditor
            value={draft.descriptionRich ?? ''}
            onChange={(html) => update('descriptionRich', html)}
            minHeight={100}
            entityLinks
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            AI description
          </label>
          <textarea
            value={draft.aiDescription ?? ''}
            onChange={(e) => update('aiDescription', e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </FormSection>

      <FormSection title="Media">
        {/* Images upload under the manufacturer's id, so they wait for the first
            save — an upload keyed by the name-derived slug was left orphaned when
            the name changed or the create was cancelled. Same rule as brands. */}
        {isNew ? (
          <p className="text-xs text-gray-500 italic border border-dashed border-gray-300 rounded-lg px-3 py-2">
            Logo and hero image can be added once the manufacturer is created.
          </p>
        ) : (
          <>
            <ManufacturerImageField
              label="Logo"
              field="logo"
              value={draft.logo ?? ''}
              manufacturerId={computedSlug}
              onChange={(url) => update('logo', url)}
              sourceUrl={draft.logoSourceUrl ?? ''}
              onSourceUrlChange={(url) => update('logoSourceUrl', url)}
            />

            <ManufacturerImageField
              label="Hero image"
              field="hero"
              value={draft.heroImage ?? ''}
              manufacturerId={computedSlug}
              onChange={(url) => update('heroImage', url)}
              sourceUrl={draft.heroImageSourceUrl ?? ''}
              onSourceUrlChange={(url) => update('heroImageSourceUrl', url)}
            />
          </>
        )}
      </FormSection>

      <FormSection title="Location">
        <CountrySelect
          value={draft.country ?? ''}
          onChange={(v) => update('country', v)}
          label="Country"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={draft.city ?? ''}
              onChange={(e) => update('city', e.target.value)}
              placeholder="Headquarters city"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State / region</label>
            <input
              type="text"
              value={draft.stateRegion ?? ''}
              onChange={(e) => update('stateRegion', e.target.value)}
              placeholder="Swiss canton, Japanese prefecture, US state…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assembly location
          </label>
          <input
            type="text"
            value={draft.assemblyLocation ?? ''}
            onChange={(e) => update('assemblyLocation', e.target.value)}
            placeholder="Where the movements are assembled (ETA: Grenchen; Miyota: Kaizuka…)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </FormSection>

      <FormSection title="History & Status">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Founded (year)
          </label>
          <input
            type="number"
            value={draft.founded ?? ''}
            onChange={(e) =>
              update('founded', e.target.value ? Number(e.target.value) : undefined)
            }
            placeholder="e.g., 1793"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div>
          <label
            htmlFor="manufacturerStatus"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Status
          </label>
          <select
            id="manufacturerStatus"
            value={draft.status ?? 'active'}
            onChange={(e) => update('status', e.target.value as BrandStatus)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="active">Active</option>
            <option value="dormant">Dormant — exists but not producing</option>
            <option value="defunct">Defunct — out of business (e.g. Valjoux)</option>
            <option value="revived">Revived — relaunched, producing again</option>
            <option value="absorbed">Absorbed — folded into another maker</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Display-only marker. Non-active manufacturers stay browsable in Explore.
          </p>
          {draft.status !== 'defunct' && existingSlug && (() => {
            const revivedBy = parentOptions.filter((m) =>
              m.heritageBrandIds?.includes(existingSlug),
            );
            return revivedBy.length > 0 ? (
              <p className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                ⚠ {revivedBy.length} manufacturer{revivedBy.length === 1 ? '' : 's'} list this as
                heritage ({revivedBy.map((m) => m.displayName || m.name).join(', ')}). Heritage links
                expect a defunct source — changing status away from Defunct leaves them dangling.
              </p>
            ) : null;
          })()}
        </div>

        {(draft.status === 'defunct' ||
          draft.status === 'dormant' ||
          draft.status === 'absorbed') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year ceased
            </label>
            <input
              type="number"
              value={draft.yearCeased ?? ''}
              onChange={(e) =>
                update('yearCeased', e.target.value ? Number(e.target.value) : undefined)
              }
              placeholder="e.g., 1988"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Shown for defunct, dormant &amp; absorbed manufacturers. Leave blank
              if unknown.
            </p>
          </div>
        )}

        {draft.status === 'revived' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year revived
              </label>
              <input
                type="number"
                value={draft.yearRevived ?? ''}
                onChange={(e) =>
                  update('yearRevived', e.target.value ? Number(e.target.value) : undefined)
                }
                placeholder="e.g. 2016"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Predecessor manufacturer
              </label>
              <input
                type="text"
                value={draft.predecessorManufacturer ?? ''}
                onChange={(e) => update('predecessorManufacturer', e.target.value)}
                placeholder="The earlier manufacturer this one revives"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Only for a revival of an earlier, distinct manufacturer — not a rename.
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Founder
          </label>
          <input
            type="text"
            value={draft.founder?.name ?? ''}
            onChange={(e) => update('founder', { name: e.target.value })}
            placeholder='Single string — co-founders packed in ("Alice Smith and Bob Jones")'
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Many manufacturers emerged from mergers of older ebauche houses with no
            single founder — leave blank in that case rather than invent one.
          </p>
        </div>

        {/* Timeline. Gated like its siblings: the events are written straight to
            the document by their own Save, and an image upload needs a stable
            manufacturer id to key its storage path on — neither is available
            before the manufacturer exists. */}
        {!isNew && initial && (
          <ManufacturerTimelineSection
            manufacturer={initial}
            onManufacturerChange={onManufacturerChange}
          />
        )}
      </FormSection>

      <FormSection title="Ownership & Relationships">
        <div>
          <label
            htmlFor="manufacturerOwnershipType"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Ownership type
          </label>
          <select
            id="manufacturerOwnershipType"
            value={draft.ownershipType ?? ''}
            onChange={(e) =>
              update(
                'ownershipType',
                e.target.value === ''
                  ? undefined
                  : (e.target.value as NonNullable<MovementManufacturer['ownershipType']>),
              )
            }
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm bg-white"
          >
            <option value="">— Not set —</option>
            <option value="independent">Independent</option>
            <option value="family-owned">Family-owned</option>
            <option value="private-equity">Private equity</option>
            <option value="public">Public</option>
            <option value="subsidiary">Subsidiary</option>
          </select>
          {draft.ownershipType === 'subsidiary' && (
            <p className="text-xs text-gray-500 mt-1">
              A subsidiary should have its Parent Organization set as well.
            </p>
          )}
        </div>

        <div>
          <ParentOrgPicker
            label="Parent organization"
            value={{ parentOrgId: draft.parentOrgId ?? '', parentOrg: draft.parentOrg ?? '' }}
            index={parentOrgIndex}
            loading={parentOrgsLoading}
            error={parentOrgsError}
            onChange={(next) => {
              update('parentOrg', next.parentOrg);
              update('parentOrgId', next.parentOrgId);
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Parent manufacturer
          </label>
          <select
            value={draft.parentManufacturerId ?? ''}
            onChange={(e) => update('parentManufacturerId', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="">— None —</option>
            {(() => {
              const sorted = [...parentOptions].sort((a, b) => {
                const aGeneric = a.isGeneric ?? false;
                const bGeneric = b.isGeneric ?? false;
                if (aGeneric !== bGeneric) return aGeneric ? -1 : 1;
                const aName = (a.displayName || a.name || '').toLowerCase();
                const bName = (b.displayName || b.name || '').toLowerCase();
                return aName.localeCompare(bName);
              });
              const generic = sorted.filter((m) => m.isGeneric);
              const inHouse = sorted.filter((m) => !m.isGeneric);
              return (
                <>
                  {generic.length > 0 && (
                    <optgroup label="Generic Manufacturers">
                      {generic.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.displayName ?? m.name} ({m.id})
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {inHouse.length > 0 && (
                    <optgroup label="In-House Manufacturers">
                      {inHouse.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.displayName ?? m.name} ({m.id})
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              );
            })()}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            For sub-brands rolled up under a parent — e.g. Valjoux under ETA.
            Phase 4 wires consumer reads.
          </p>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isGenericValue}
            onChange={(e) => setIsGenericOverride(e.target.checked)}
            className="rounded text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">
            Generic / third-party manufacturer (ETA, Sellita, Miyota…)
          </span>
        </label>

        {/* Heritage / lineage — only defunct or absorbed manufacturers are selectable. */}
        <HeritagePicker
          label="Heritage"
          helpText="Manufacturers this one revives or carries lineage from. Only defunct manufacturers can be linked."
          emptyLabel="No heritage manufacturers"
          noun="manufacturers"
          values={draft.heritageBrandIds ?? []}
          onChange={(next) => update('heritageBrandIds', next)}
          options={parentOptions.map((m) => ({
            id: m.id,
            name: m.displayName || m.name,
            status: m.status,
            subtitle: m.id,
          }))}
        />

        <BrandIdsManualIncludePicker
          values={effectiveInclude}
          onChange={handleIncludeChange}
          disabledHint={
            isGenericValue
              ? 'Manufacturer is marked generic — the derivation gate ignores this list. Generics are allowed for any brand.'
              : null
          }
        />
      </FormSection>

      <FormSection title="Commerce & Links">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <input
            type="url"
            value={draft.website ?? ''}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instagram URL
          </label>
          <input
            type="url"
            value={draft.instagramUrl ?? ''}
            onChange={(e) => update('instagramUrl', e.target.value)}
            placeholder="https://www.instagram.com/<handle>/"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>
      </FormSection>

      <FormSection title="Catalogue">
        {!isNew && initial && (
          <ManufacturerDataHealthSection
            manufacturer={initial}
            onManufacturerChange={onManufacturerChange}
          />
        )}

        {/* Tier names save immediately (one transaction per change), so like the
            timeline they need a manufacturer that already exists. */}
        {!isNew && initial && (
          <ManufacturerTierNamesSection
            manufacturer={initial}
            onManufacturerChange={onManufacturerChange}
          />
        )}

        {!isNew && existingSlug && (
          <CalibreFamiliesSection manufacturerId={existingSlug} />
        )}

        {/* The lineage gate (memo D4). Off for makers that renumber outright
            (Rolex, Omega, AP); on, the calibre editor shows a Revisions card for
            every calibre this maker makes. Saved with the form. */}
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={draft.supportsLineage ?? false}
            onChange={(e) => update('supportsLineage', e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-sm text-gray-700">
            <span className="font-medium">Supports lineage</span>
            <span className="block text-xs text-gray-500">
              This maker issues successive revisions of a calibre (ETA 2824 → 2824-2). Shows revision lines here and a
              Revisions card on each of its calibres.
            </span>
          </span>
        </label>

        {draft.supportsLineage && (
          <RevisionTermsField
            singular={draft.revisionTermSingular ?? ''}
            plural={draft.revisionTermPlural ?? ''}
            onChange={(singular, plural) => {
              update('revisionTermSingular', singular);
              update('revisionTermPlural', plural);
            }}
          />
        )}

        {/* Lines save immediately, so they need the saved gate and an existing
            manufacturer. */}
        {!isNew && existingSlug && initial?.supportsLineage === true && draft.supportsLineage && (
          <RevisionLinesSection
            manufacturerId={existingSlug}
            manufacturerName={draft.displayName || draft.name}
            terms={revisionTerms(initial)}
          />
        )}
        {!isNew && draft.supportsLineage && initial?.supportsLineage !== true && (
          <p className="text-xs text-gray-500 italic">Save to start adding revision lines.</p>
        )}

        {!isNew && existingSlug && (
          <ElectronicModulesSection manufacturerId={existingSlug} />
        )}
      </FormSection>

      <FormSection title="Visibility & Admin">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.needsReview ?? false}
            onChange={(e) => update('needsReview', e.target.checked)}
            className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
          />
          <span
            className="text-sm text-purple-700"
            title="Audit worklist flag — set when this manufacturer needs manual lineage/status verification"
          >
            Needs review
          </span>
        </label>

        {/* Curator documentation — Confluence pages surfaced back on the calibre
            screens where this manufacturer's naming conventions get applied. */}
        <DocsLinksEditor
          value={draft.docs ?? []}
          onChange={(next) => update('docs', next)}
          disabled={saving}
          entityNoun="manufacturer"
        />
      </FormSection>

      <EnrichHintDialog
        isOpen={showEnrichHint}
        entityNoun="manufacturer"
        descriptionLinkCount={countEntityLinks(draft.descriptionRich)}
        hint={enrichHint}
        onHintChange={setEnrichHint}
        onCancel={() => setShowEnrichHint(false)}
        onRun={() => {
          setShowEnrichHint(false);
          void handleAIEnrich(enrichHint);
        }}
      />

      <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
        <Button
          variant="secondary"
          onClick={() => setShowEnrichHint(true)}
          loading={aiLoading}
          disabled={!draft.name.trim() || saving}
          title={!draft.name.trim() ? 'Enter a name first' : 'Ask Claude to fill in missing fields'}
          className="mr-auto"
        >
          ✨ Enrich with AI
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={saving} disabled={!canSave}>
          {isNew ? 'Create' : 'Save'}
        </Button>
      </div>

      <Modal
        isOpen={showAISuggestions}
        onClose={() => setShowAISuggestions(false)}
        title="AI Suggestions"
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Review Claude&apos;s suggestions below. Check the rows you want to apply,
            then click <span className="font-medium">Apply Selected</span>. Nothing is
            saved until you click {isNew ? 'Create' : 'Save'} on the manufacturer form.
          </p>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-12"></th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Field
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Current
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Suggested
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-24">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.entries(aiSuggestions).map(([key, value]) => {
                  const handler = aiFieldHandlers[key];
                  const label = handler?.label ?? key;
                  const current = handler?.currentDisplay ?? '';
                  const suggested = handler?.formatSuggested
                    ? handler.formatSuggested(value)
                    : typeof value === 'object'
                      ? JSON.stringify(value)
                      : typeof value === 'boolean'
                        ? (value ? 'Yes' : 'No')
                        : String(value);
                  const confidence = aiConfidence[key] || 'medium';
                  const badgeVariant: 'success' | 'warning' | 'default' =
                    confidence === 'high'
                      ? 'success'
                      : confidence === 'medium'
                        ? 'warning'
                        : 'default';
                  const unsupported = !handler;
                  return (
                    <tr key={key} className={unsupported ? 'opacity-50' : ''}>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!aiAccepted[key]}
                          disabled={unsupported}
                          onChange={(e) =>
                            setAiAccepted((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {label}
                        {unsupported && (
                          <span className="ml-2 text-xs text-gray-400">
                            (not editable here)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 max-w-xs truncate">
                        {current || <span className="italic text-gray-400">empty</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-900 max-w-md whitespace-pre-wrap break-words">
                        {suggested}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={badgeVariant}>{confidence}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="secondary" onClick={() => setShowAISuggestions(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyAISuggestions}>Apply Selected</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

interface BrandIdsManualIncludePickerProps {
  values: string[];
  onChange: (next: string[]) => void;
  /** Optional muted-text hint shown when the field is informational (e.g., mfr is generic). */
  disabledHint: string | null;
}

function BrandIdsManualIncludePicker({
  values,
  onChange,
  disabledHint,
}: BrandIdsManualIncludePickerProps) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    getWatchBrands()
      .then((all) => {
        if (cancelled) return;
        const sorted = [...all].sort((a, z) =>
          (a.displayName || a.name || '').localeCompare(z.displayName || z.name || ''),
        );
        setBrands(sorted);
      })
      .catch((err) => {
        console.error('Failed to load brands for picker', err);
        toast.error('Failed to load brands');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSet = useMemo(() => new Set(values), [values]);
  const brandById = useMemo(() => {
    const m = new Map<string, WatchBrand>();
    for (const b of brands) m.set(b.id, b);
    return m;
  }, [brands]);

  // Algolia-backed: adds typo tolerance, prefix matching, accent folding and
  // parent-org search on top of what the substring filter covered. That same
  // substring filter (name, display name, id, alternative names, abbreviations)
  // is the fallback when Algolia is unreachable.
  const { results: filtered } = useBrandSearch(brands, search);

  const toggle = (brandId: string) => {
    const next = new Set(selectedSet);
    if (next.has(brandId)) next.delete(brandId);
    else next.add(brandId);
    onChange(Array.from(next).sort());
  };

  const removeChip = (brandId: string) => {
    const next = values.filter((v) => v !== brandId);
    onChange(next);
  };

  // Selected brands not in the loaded list (e.g., orphaned id) — still removable.
  const orphanedSelected = values.filter((id) => !brandById.has(id));

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Allowed Brands (for in-house manufacturers only)
      </label>
      <p className="text-xs text-gray-500 mb-2">
        If this manufacturer is non-generic, only these brands can be linked to it via the
        brand ↔ manufacturer M:N derivation. Generic manufacturers ignore this list. Leave
        empty if you haven&apos;t curated this yet — the derivation gate will skip linking
        for non-generics until populated.
      </p>
      {disabledHint && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          {disabledHint}
        </p>
      )}

      {/* Selected chips */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map((id) => {
            const b = brandById.get(id);
            const label = b ? (b.displayName || b.name) : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 bg-brand-50 border border-brand-200 text-brand-800 text-xs rounded-full pl-2 pr-1 py-0.5"
              >
                <span title={id}>{label}</span>
                <button
                  type="button"
                  onClick={() => removeChip(id)}
                  aria-label={`Remove ${label}`}
                  className="rounded-full hover:bg-brand-100 w-4 h-4 inline-flex items-center justify-center text-brand-700"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={loading ? 'Loading brands…' : 'Search brands…'}
        disabled={loading}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 mb-1"
      />

      <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
        {loading ? (
          <div className="p-3 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-sm text-gray-500">No brands match.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((b) => {
              const checked = selectedSet.has(b.id);
              return (
                <li key={b.id}>
                  <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(b.id)}
                      className="rounded text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-800 flex-1">
                      {b.displayName || b.name}
                    </span>
                    <code className="text-xs text-gray-400">{b.id}</code>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {orphanedSelected.length > 0 && (
        <p className="text-xs text-amber-700 mt-1">
          Selected but not in current brand list: {orphanedSelected.join(', ')}
        </p>
      )}
    </div>
  );
}
